import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Play,
  Loader2,
  ArrowLeft,
  Check,
  CheckCircle2,
  Edit3,
  Eye,
  Radio,
  Square,
  Trash2,
} from 'lucide-react';
import { JsonSchemaForm } from './JsonSchemaForm';
import { JsonResultView } from './JsonResultView';
import { InlineJsonEditor } from './InlineJsonEditor';
import type { CommandSpec } from '@/lib/types';
import { localizeCommand } from '@/lib/i18n/commands';
import { tsLocal } from '@/lib/format';
import clsx from 'clsx';

/**
 * Single `evt` frame surfaced to a subscription-mode CmdRunCard. The
 * parent flattens the device's WS event log into this shape so the
 * card filters and renders without knowing about the wire format.
 */
export interface CmdSubEvent {
  /** Hub-generated subscription id; must match the card's `subId`. */
  subId: string;
  /** Receive time (epoch ms). */
  time: number;
  /** Application payload — whatever the device emitted as `evt.payload`. */
  payload: unknown;
}

interface Props {
  spec: CommandSpec;
  defaultArgs?: Record<string, unknown>;
  /** When provided, runs the command and resolves with the result payload. */
  onRun: (args: Record<string, unknown>) => Promise<unknown>;
  /** When provided, surfaced as raw JSON below the form. */
  initialResult?: unknown;
  disabled?: boolean;
  disabledReason?: string;
  hideInlineActions?: boolean;
  /** Notified whenever the running flag flips. */
  onRunningChange?: (running: boolean) => void;
  /**
   * Click handler for the "← 加载当前值" button. Only renders when
   * {@link CommandSpec.hasSnapshot} is true. Should return the partial
   * Args POJO the device's snapshot lambda emitted; this card merges it
   * field-wise into the form state.
   */
  onSnapshot?: (args: Record<string, unknown>) => Promise<Record<string, unknown> | undefined>;
  /**
   * Set of dep-cmd names that have already been observed to succeed in
   * the current session. Each chip in the {@code spec.dependsOn} row
   * flips green when its name is present here.
   */
  satisfiedDeps?: Set<string>;
  /**
   * Click handler for an individual dep chip's inline ▷ run button.
   * The parent typically dispatches the dep command and updates
   * {@link satisfiedDeps} on success.
   */
  onRunDep?: (depName: string) => void;
  /** Lookup function so chip labels can show the dep's display name. */
  resolveDep?: (depName: string) => CommandSpec | undefined;
  /**
   * Open a long-lived subscription on the device. Only consulted when
   * `spec.subscribe` is true — the card swaps the "运行" button for a
   * "订阅 / 取消订阅" pair and renders `subEvents` matching the
   * returned id as a live event log.
   */
  onSubscribe?: (args: Record<string, unknown>) => Promise<string>;
  /** Close the subscription previously opened with {@link onSubscribe}. */
  onUnsubscribe?: (subId: string) => Promise<void>;
  /**
   * Flat list of every `evt` frame the parent has buffered (newest
   * first or chronological — either works, we filter and sort). The
   * card displays the subset whose `subId` matches the active sub.
   */
  subEvents?: CmdSubEvent[];
}

export interface CmdRunCardHandle {
  run: () => Promise<void>;
}

export const CmdRunCard = forwardRef<CmdRunCardHandle, Props>(function CmdRunCard(
  {
    spec,
    onRun,
    defaultArgs,
    initialResult,
    disabled,
    disabledReason,
    hideInlineActions,
    onRunningChange,
    onSnapshot,
    satisfiedDeps,
    onRunDep,
    resolveDep,
    onSubscribe,
    onUnsubscribe,
    subEvents,
  },
  ref
) {
  const [args, setArgs] = useState<Record<string, unknown>>({ ...(defaultArgs ?? {}) });
  const [result, setResult] = useState<unknown>(initialResult);
  const [error, setError] = useState<string | undefined>();
  const [running, setRunning] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  /** Editable mode for the result view (off → JSON, on → JsonSchemaForm). */
  const [resultEdit, setResultEdit] = useState(false);
  const [editableResult, setEditableResult] = useState<unknown>(initialResult);
  /**
   * Render mode for the args form. {@code 'inline'} → compact JSON tree
   * with double-click edits ({@link InlineJsonEditor}). {@code 'form'} →
   * full {@link JsonSchemaForm} with labelled rows. Defaults to inline
   * for `hasSnapshot` commands (the snapshot lambda fills the values so
   * users want to see / tweak them, not type them from scratch), and to
   * form for commands the user types from scratch.
   */
  const [argsView, setArgsView] = useState<'inline' | 'form'>(
    spec.hasSnapshot ? 'inline' : 'form'
  );
  useEffect(() => {
    setArgsView(spec.hasSnapshot ? 'inline' : 'form');
  }, [spec.name, spec.hasSnapshot]);

  /**
   * Active subscription id (set after a successful `onSubscribe`).
   * Drives the "订阅 ↔ 取消订阅" button toggle and the evt-log render.
   */
  const [subId, setSubId] = useState<string | undefined>();
  /**
   * "Min event time" baseline — events whose `time` is strictly less
   * than this value are dropped. Implemented as a hook so the
   * subscription can be reset by the "清空" button without losing
   * future frames. Defaults to {@code 0} so the first burst of events
   * (which may already be sitting in the parent's events buffer by
   * the time `subId` settles in React state) is shown rather than
   * silently filtered out.
   */
  const [subEventCutoff, setSubEventCutoff] = useState<number>(0);
  const [subscribing, setSubscribing] = useState(false);
  const subIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    subIdRef.current = subId;
  }, [subId]);

  const locale = localizeCommand(spec.name);
  const titleCn = locale?.title;
  const descCn = locale?.desc;

  // Reset editableResult whenever a new result lands so editing always
  // starts from the most recent canonical value.
  useEffect(() => {
    setEditableResult(result);
    setResultEdit(false);
  }, [result]);

  const run = useCallback(async () => {
    if (running || disabled) return;
    setError(undefined);
    setRunning(true);
    onRunningChange?.(true);
    try {
      // Strip any keys not declared by this command's schema. Belt and
      // suspenders against `args` retaining stale fields after a spec switch.
      const allowed = spec.args.schema?.properties;
      const safeArgs = allowed
        ? Object.fromEntries(Object.entries(args).filter(([k]) => k in allowed))
        : args;
      const r = await onRun(safeArgs);
      setResult(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(false);
      onRunningChange?.(false);
    }
  }, [args, disabled, onRun, onRunningChange, running, spec]);

  useImperativeHandle(ref, () => ({ run }), [run]);

  useEffect(() => {
    if (!disabled) setError((cur) => (cur === disabledReason ? undefined : cur));
  }, [disabled, disabledReason]);

  const startSubscribe = useCallback(async () => {
    if (!onSubscribe || subscribing || subId) return;
    setSubscribing(true);
    setError(undefined);
    try {
      const allowed = spec.args.schema?.properties;
      const safeArgs = allowed
        ? Object.fromEntries(Object.entries(args).filter(([k]) => k in allowed))
        : args;
      const id = await onSubscribe(safeArgs);
      setSubId(id);
      // Don't time-gate the very first frames — subId uniqueness is
      // already strong enough on its own. A separate cutoff knob is
      // raised only when the user explicitly clicks "清空".
      setSubEventCutoff(0);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubscribing(false);
    }
  }, [args, onSubscribe, spec, subId, subscribing]);

  const stopSubscribe = useCallback(
    async (idOverride?: string) => {
      const id = idOverride ?? subId;
      if (!id || !onUnsubscribe) return;
      try {
        await onUnsubscribe(id);
      } catch (e) {
        // Surface but don't block clearing local state — the handle is
        // gone on the hub-side regardless.
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setSubId(undefined);
        setSubEventCutoff(0);
      }
    },
    [onUnsubscribe, subId]
  );

  // Always tear down the subscription when the card unmounts (spec
  // switch, package change, page leave). The hub's `/control/unsub`
  // accepts unknown ids silently, so this is safe.
  //
  // CRITICAL: depend on `[]`, NOT `[onUnsubscribe]`. The parent passes
  // an inline arrow for `onUnsubscribe` and re-creates it on every
  // render, so a dep of `[onUnsubscribe]` would fire the cleanup on
  // every parent re-render and cancel an active subscription within
  // milliseconds of it being established. We bridge to the latest prop
  // via {@link onUnsubscribeRef} so the unmount path still calls the
  // freshest handler without recreating the effect.
  const onUnsubscribeRef = useRef(onUnsubscribe);
  useEffect(() => {
    onUnsubscribeRef.current = onUnsubscribe;
  }, [onUnsubscribe]);
  useEffect(() => {
    return () => {
      const id = subIdRef.current;
      const unsub = onUnsubscribeRef.current;
      if (id && unsub) {
        // Fire-and-forget — there is no React state to update after
        // unmount, and the hub handles dropped sockets gracefully.
        void unsub(id).catch(() => undefined);
      }
    };
  }, []);

  const relevantEvents = useMemo(() => {
    if (!subId || !subEvents) return [];
    return subEvents
      .filter((e) => e.subId === subId && e.time >= subEventCutoff)
      .sort((a, b) => b.time - a.time)
      .slice(0, 200);
  }, [subEvents, subId, subEventCutoff]);

  const loadSnapshot = useCallback(async () => {
    if (!onSnapshot || snapshotting) return;
    setSnapshotting(true);
    setError(undefined);
    try {
      const allowed = spec.args.schema?.properties;
      const safeArgs = allowed
        ? Object.fromEntries(Object.entries(args).filter(([k]) => k in allowed))
        : args;
      const filled = await onSnapshot(safeArgs);
      if (filled && typeof filled === 'object') {
        // Merge so values the user already typed are not clobbered when
        // the snapshot lambda returns a partial response.
        setArgs((prev) => ({ ...prev, ...filled }));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSnapshotting(false);
    }
  }, [args, onSnapshot, snapshotting, spec]);

  /**
   * Auto-load the snapshot when the user opens a {@code hasSnapshot}
   * command — surface current values immediately so the SPA reads as a
   * "tweak this value" UI rather than a "type from scratch" form.
   * Re-runs whenever the active spec changes (e.g. picking a different
   * command); inflight guard prevents a double fire on remount.
   */
  const autoSnapshotFiredRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!onSnapshot) return;
    if (!spec.hasSnapshot) return;
    if (autoSnapshotFiredRef.current === spec.name) return;
    autoSnapshotFiredRef.current = spec.name;
    void loadSnapshot();
    // intentionally omit `loadSnapshot` from deps — it depends on `args`
    // which we don't want to trigger another auto-fire on
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec.name, spec.hasSnapshot, onSnapshot]);

  const depChips = useMemo(() => {
    if (!spec.dependsOn || spec.dependsOn.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1.5">
        {spec.dependsOn.map((dep) => {
          const ok = satisfiedDeps?.has(dep) ?? false;
          const depSpec = resolveDep?.(dep);
          return (
            <div
              key={dep}
              className={clsx(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition',
                ok
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-500'
                  : 'border-amber-500/40 bg-amber-500/5 text-amber-500'
              )}
              title={
                ok
                  ? `已运行过 ${dep}，可以放心执行主命令`
                  : `建议先运行 ${dep}（${depSpec?.displayName ?? '前置命令'}）`
              }
            >
              {ok ? <CheckCircle2 className="h-3 w-3" /> : <Loader2 className="h-3 w-3" />}
              <code className="font-mono">{dep}</code>
              {depSpec?.displayName && (
                <span className="text-ink-dim">· {depSpec.displayName}</span>
              )}
              {onRunDep && (
                <button
                  type="button"
                  className="rounded p-0.5 hover:bg-bg-soft"
                  onClick={() => onRunDep(dep)}
                  title={`运行依赖：${dep}`}
                >
                  <Play className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  }, [spec.dependsOn, satisfiedDeps, resolveDep, onRunDep]);

  return (
    <div className="card-pad space-y-3">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <code className="font-mono text-sm text-accent">{spec.name}</code>
            <span className="badge">{spec.side}</span>
            <span className="badge">{spec.category}</span>
            {spec.subscribe && (
              <span className="badge border-sky-500 text-sky-500">订阅</span>
            )}
            {spec.hasSnapshot && (
              <span
                className="badge inline-flex items-center gap-1 border-emerald-500 text-emerald-500"
                title="此命令声明了 .snapshot 回调，可点击表单顶部的「← 加载当前值」预填"
              >
                <Eye className="h-3 w-3" /> 可预览
              </span>
            )}
          </div>
          <p className="text-sm text-ink">
            {titleCn ?? spec.displayName}
            {titleCn && spec.displayName && titleCn !== spec.displayName && (
              <span className="ml-2 text-xs text-ink-dim">{spec.displayName}</span>
            )}
          </p>
          <p className="whitespace-pre-wrap text-xs text-ink-muted">
            {descCn ?? spec.description}
          </p>
        </div>
      </div>

      <div className="hairline -mx-4" />

      {depChips}

      <div className="flex items-center gap-2">
        {spec.hasSnapshot && (
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/5 px-2 py-1 text-[11px] text-emerald-500 transition hover:bg-emerald-500/15 disabled:opacity-60"
            disabled={snapshotting || disabled}
            onClick={() => void loadSnapshot()}
            title="发送 mode:preview 帧，由设备侧 snapshot lambda 读取当前状态。带 .snapshot 的命令被选中时会自动跑一次。"
          >
            {snapshotting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <ArrowLeft className="h-3 w-3" />
            )}
            {snapshotting ? '加载中…' : '重新加载当前值'}
          </button>
        )}
        <div className="ml-auto inline-flex overflow-hidden rounded-md border border-line text-[11px]">
          <button
            type="button"
            className={clsx(
              'px-2 py-0.5 transition',
              argsView === 'inline'
                ? 'bg-accent-soft text-accent'
                : 'text-ink-dim hover:bg-bg-soft hover:text-ink'
            )}
            onClick={() => setArgsView('inline')}
            title="紧凑 JSON 视图：双击字段值直接编辑"
          >
            JSON
          </button>
          <button
            type="button"
            className={clsx(
              'px-2 py-0.5 transition',
              argsView === 'form'
                ? 'bg-accent-soft text-accent'
                : 'text-ink-dim hover:bg-bg-soft hover:text-ink'
            )}
            onClick={() => setArgsView('form')}
            title="表单视图：每个字段一行带标签"
          >
            表单
          </button>
        </div>
      </div>

      {argsView === 'inline' ? (
        <InlineJsonEditor
          schema={spec.args.schema}
          value={args}
          onChange={(v) =>
            setArgs((v as Record<string, unknown> | null | undefined) ?? {})
          }
        />
      ) : (
        <JsonSchemaForm
          schema={spec.args.schema}
          value={args}
          onChange={(v) => setArgs(v as Record<string, unknown>)}
          example={spec.args.example as Record<string, unknown> | undefined}
          fieldLocale={locale?.fields}
        />
      )}

      {error && (
        <p className="text-xs text-rose-500" role="alert">
          {error}
        </p>
      )}
      {!error && disabled && disabledReason && (
        <p className="text-xs text-amber-500">{disabledReason}</p>
      )}

      {result !== undefined && result !== null && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-medium text-ink-muted">运行结果</h4>
            <button
              type="button"
              className={clsx(
                'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] transition',
                resultEdit
                  ? 'bg-accent-soft text-accent'
                  : 'text-ink-dim hover:bg-bg-soft hover:text-ink'
              )}
              onClick={() => setResultEdit((v) => !v)}
              title={resultEdit ? '回到 JSON 视图' : '切换为可编辑视图'}
            >
              <Edit3 className="h-3 w-3" />
              {resultEdit ? '查看 JSON' : '编辑结果'}
            </button>
          </div>
          {resultEdit ? (
            <JsonSchemaForm
              schema={spec.result.schema}
              value={editableResult}
              onChange={setEditableResult}
            />
          ) : (
            <JsonResultView value={result} />
          )}
        </div>
      )}
      {/*
        Defensive fallback for the rare case where a legacy command still
        returns a `null` result (the WsHubResultMustNotBeVoid detekt rule
        forbids this at compile time, but third-party hubs may still ship
        old SDKs). Surface a small badge instead of a literal "null".
      */}
      {result === null && !error && (
        <p
          className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-500"
          title="设备已确认命令完成，但未返回任何负载。建议改用 WsAck / 业务 POJO 作为结果类型。"
        >
          <Check className="h-3 w-3" />
          已完成 · 无返回载荷
        </p>
      )}

      {spec.subscribe && subId && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted">
              <Radio className="h-3 w-3 text-sky-500" />
              事件流 ({relevantEvents.length}) ·{' '}
              <code className="text-[10px] text-ink-dim">{subId.slice(0, 8)}</code>
              {subEvents && subEvents.length > 0 && (
                <span
                  className="ml-1 text-[10px] text-ink-dim"
                  title="SPA 收到的总 evt 帧数（不区分 subId / 过滤），用于诊断"
                >
                  · 全部:{subEvents.length}
                </span>
              )}
            </h4>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-ink-dim hover:bg-bg-soft hover:text-ink"
              onClick={() => setSubEventCutoff(Date.now())}
              title="清空当前事件视图（保留订阅，下一帧起重新累计）"
            >
              <Trash2 className="h-3 w-3" />
              清空
            </button>
          </div>
          <ul className="max-h-72 min-w-0 space-y-1 overflow-auto rounded border border-line bg-bg-soft/40 p-1">
            {relevantEvents.length === 0 && (
              <li className="px-1 py-2 text-center text-[11px] text-ink-muted">
                已订阅，等待第一帧 evt…
              </li>
            )}
            {relevantEvents.map((e, i) => (
              <li
                key={`${e.time}-${i}`}
                className="min-w-0 rounded border border-line/60 bg-bg-card p-1.5 text-[11px]"
              >
                <div className="flex items-center justify-between text-ink-muted">
                  <span className="font-mono text-sky-500">evt</span>
                  <span>{tsLocal(new Date(e.time).toISOString())}</span>
                </div>
                {e.payload !== null && e.payload !== undefined && (
                  <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap break-words rounded bg-bg-soft p-1 font-mono text-[10px]">
                    {JSON.stringify(e.payload, null, 2)}
                  </pre>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!hideInlineActions && (
        <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
          {spec.subscribe ? (
            subId ? (
              <button
                className="inline-flex items-center gap-1 rounded border border-rose-500/40 bg-rose-500/5 px-3 py-1 text-sm text-rose-500 hover:bg-rose-500/15 disabled:opacity-60"
                onClick={() => void stopSubscribe()}
                title="发送 unsub 帧，关闭设备端订阅"
              >
                <Square className="h-4 w-4" />
                <span>取消订阅</span>
              </button>
            ) : (
              <button
                className="btn-primary"
                disabled={subscribing || disabled}
                title={disabled ? disabledReason : '发送 sub 帧，建立长连接订阅'}
                onClick={() => void startSubscribe()}
              >
                {subscribing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Radio className="h-4 w-4" />
                )}
                <span>订阅</span>
              </button>
            )
          ) : (
            <button
              className="btn-primary"
              disabled={running || disabled}
              title={disabled ? disabledReason : undefined}
              onClick={run}
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              <span>运行</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
});
